import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardBody, CardHeader, Row, Col, FormGroup, Label, Spinner, Alert, Input, Collapse, Button } from 'reactstrap';
import { useGetUserPermissions } from '../api/users/service';

const PERMISSION_GROUP_ORDER = [
  'accounts',
  'customers',
  'deals',
  'inventory',
  'channels',
  'settings',
];

const INVENTORY_SUBGROUP_ORDER = ['inventory', 'inventory_parameters'];

const normalizeGroupName = (group) =>
  (group?.name || group?.key || '').trim().toLowerCase().replace(/\s+/g, '_');

const getPermissionGroupSortIndex = (group) => {
  const normalized = normalizeGroupName(group);

  const mainIndex = PERMISSION_GROUP_ORDER.indexOf(normalized);
  if (mainIndex !== -1) return mainIndex * 100;

  // Inventory block: inventory first, then inventory_parameters, then other inventory_* groups
  if (
    normalized === 'inventory' ||
    normalized.startsWith('inventory_') ||
    normalized.startsWith('inventory ')
  ) {
    const inventoryMainIndex = PERMISSION_GROUP_ORDER.indexOf('inventory');
    for (let i = INVENTORY_SUBGROUP_ORDER.length - 1; i >= 0; i -= 1) {
      const keyword = INVENTORY_SUBGROUP_ORDER[i];
      if (normalized === keyword || normalized.startsWith(`${keyword}_`)) {
        return inventoryMainIndex * 100 + i;
      }
    }
    return inventoryMainIndex * 100 + INVENTORY_SUBGROUP_ORDER.length;
  }

  for (let i = 0; i < PERMISSION_GROUP_ORDER.length; i++) {
    const keyword = PERMISSION_GROUP_ORDER[i];
    if (keyword === 'inventory') continue;
    if (normalized.startsWith(`${keyword}_`) || normalized.startsWith(`${keyword} `)) {
      return i * 100 + 10;
    }
  }

  return PERMISSION_GROUP_ORDER.length * 100 + 500;
};

const useLightTheme = () => {
  const [isLightTheme, setIsLightTheme] = useState(() =>
    typeof document !== 'undefined' && document.body.classList.contains('white-content')
  );

  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isLightTheme;
};

/**
 * @param {Object} props
 * @param {any} [props.roleDetail]
 * @param {(permissions: { [key: string]: boolean }) => void} [props.onPermissionsChange]
 * @param {boolean} [props.isNewRole]
 * @param {string} [props.selectedRole] - 'custom' or role ID
 * @param {boolean} [props.isTheme2]
 */
export default function RolePermissions({
  roleDetail,
  onPermissionsChange,
  isNewRole = false,
  selectedRole = 'custom',
  isTheme2 = false,
}) {
  const { data: permissionsData, isLoading, isError, error } = useGetUserPermissions();
  const isLightTheme = useLightTheme();
  const sectionTitleColor = isLightTheme ? '#1d253b' : 'rgba(255, 255, 255, 0.95)';
  const sectionSubtitleColor = isLightTheme ? '#6c757d' : 'rgba(255, 255, 255, 0.5)';
  const groupHeaderColor = isLightTheme ? '#1d253b' : 'rgba(255, 255, 255, 0.95)';
  const permissionLabelColor = isLightTheme ? '#1d253b' : 'rgba(255, 255, 255, 0.85)';

  const typography = useMemo(() => {
    if (!isTheme2) {
      return {
        button: { fontSize: '12px', padding: '2px 8px' },
        toggle: { fontSize: '16px', fontWeight: 'bold' },
      };
    }

    return {
      button: { padding: '2px 8px' },
      toggle: { fontWeight: 'bold' },
    };
  }, [isTheme2]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [openGroups, setOpenGroups] = useState([]); // for custom accordion

  // Format permissions for parent
  const formatPermissions = useCallback((permissionIds) => {
    const formattedPermissions = {};
    if (permissionsData?.data) {
      permissionsData.data.forEach((group) => {
        group.permissions.forEach((permission) => {
          formattedPermissions[permission.key] = permissionIds.includes(permission.id);
        });
      });
    }
    return formattedPermissions;
  }, [permissionsData]);

  // Initialize selected permissions
  useEffect(() => {
    if (isNewRole && permissionsData?.data && !isInitialized) {
      setSelectedPermissions([]);
      setIsInitialized(true);
      if (onPermissionsChange) {
        const formattedPermissions = formatPermissions([]);
        onPermissionsChange(formattedPermissions);
      }
    } else if (roleDetail?.permissions && permissionsData?.data && !isInitialized) {
      const rolePermissions = roleDetail.permissions;
      const selectedIds = [];
      permissionsData.data.forEach((group) => {
        group.permissions.forEach((permission) => {
          if (rolePermissions[permission.key] === true) {
            selectedIds.push(permission.id);
          }
        });
      });
      setSelectedPermissions(selectedIds);
      setIsInitialized(true);
    }
  }, [roleDetail, permissionsData, isNewRole, formatPermissions, isInitialized, onPermissionsChange]);

  useEffect(() => {
    setIsInitialized(false); // reset initialization when roleDetail or isNewRole changes
  }, [roleDetail, isNewRole]);

  // Open all accordion groups when adding or editing a role
  useEffect(() => {
    if (permissionsData?.data) {
      setOpenGroups(permissionsData.data.map((group) => group.id));
    }
  }, [permissionsData]);

  const permissionGroups = useMemo(() => {
    const groups = permissionsData?.data ? [...permissionsData.data] : [];
    groups.sort((a, b) => {
      const orderDiff = getPermissionGroupSortIndex(a) - getPermissionGroupSortIndex(b);
      if (orderDiff !== 0) return orderDiff;
      return (a?.name || '').localeCompare(b?.name || '', 'tr', { sensitivity: 'base' });
    });
    return groups;
  }, [permissionsData]);

  // Custom accordion toggle logic
  const toggleGroup = (id) => {
    // Only allow toggling in custom mode
    if (selectedRole === 'custom') {
      setOpenGroups((prevOpen) =>
        prevOpen.includes(id)
          ? prevOpen.filter((item) => item !== id)
          : [...prevOpen, id]
      );
    }
  };

  const handlePermissionChange = (permissionId, checked) => {
    let newSelectedPermissions;
    if (checked) {
      newSelectedPermissions = [...selectedPermissions, permissionId];
    } else {
      newSelectedPermissions = selectedPermissions.filter(id => id !== permissionId);
    }
    setSelectedPermissions(newSelectedPermissions);
    const formattedPermissions = formatPermissions(newSelectedPermissions);
    if (onPermissionsChange) {
      onPermissionsChange(formattedPermissions);
    }
  };

  // Select all permissions in a group
  const handleSelectAllInGroup = (group) => {
    const groupPermissionIds = group.permissions.map(permission => permission.id);
    const currentGroupSelected = groupPermissionIds.filter(id => selectedPermissions.includes(id));

    let newSelectedPermissions;
    if (currentGroupSelected.length === groupPermissionIds.length) {
      // If all are selected, deselect all
      newSelectedPermissions = selectedPermissions.filter(id => !groupPermissionIds.includes(id));
    } else {
      // If not all are selected, select all
      newSelectedPermissions = [...selectedPermissions];
      groupPermissionIds.forEach(id => {
        if (!newSelectedPermissions.includes(id)) {
          newSelectedPermissions.push(id);
        }
      });
    }

    setSelectedPermissions(newSelectedPermissions);
    const formattedPermissions = formatPermissions(newSelectedPermissions);
    if (onPermissionsChange) {
      onPermissionsChange(formattedPermissions);
    }
  };

  // Check if all permissions in a group are selected
  const isAllSelectedInGroup = (group) => {
    const groupPermissionIds = group.permissions.map(permission => permission.id);
    return groupPermissionIds.every(id => selectedPermissions.includes(id));
  };

  // Check if some permissions in a group are selected
  const isSomeSelectedInGroup = (group) => {
    const groupPermissionIds = group.permissions.map(permission => permission.id);
    return groupPermissionIds.some(id => selectedPermissions.includes(id));
  };

  if (isLoading) {
    return <div className="text-center my-4"><Spinner color="primary" /> Loading permissions...</div>;
  }
  if (isError) {
    return <Alert color="danger">Error loading permissions: {error?.message}</Alert>;
  }

  return (
    <div
      className={isTheme2 ? 'role-permissions-section role-permissions-section--theme2 mt-3' : 'role-permissions-section mt-3'}
    >
      <div className="mb-3">
        <b
          className="role-permissions-section__title"
          style={{ color: sectionTitleColor }}
        >
          Role Permissions
        </b>
        <small
          className="form-text d-block mt-1"
          style={{ color: sectionSubtitleColor }}
        >
          Select permissions for this role
        </small>
      </div>
      <div>
        {permissionGroups.map((group) => (
          <Card
            key={group.id}
            className="mb-2"
            style={{
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              transition: 'box-shadow 0.3s ease, transform 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (selectedRole === 'custom') {
                e.currentTarget.style.boxShadow = '0 8px 15px rgba(0, 0, 0, 0.15), 0 3px 6px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <CardHeader
              onClick={() => toggleGroup(group.id)}
              style={{
                cursor: selectedRole === 'custom' ? 'pointer' : 'default',
                userSelect: 'none',
                padding: 10,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
              }}
            >
              <div>
                <b
                  className="role-permissions-group-title"
                  style={{ color: groupHeaderColor }}
                >
                  {group.name.charAt(0).toUpperCase() + group.name.slice(1)}
                </b>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Button
                  size="sm"
                  color="primary"
                  outline
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectAllInGroup(group);
                  }}
                  style={typography.button}
                >
                  {isAllSelectedInGroup(group) ? 'Deselect All' : 'Select All'}
                </Button>
                {selectedRole === 'custom' && (
                  <span
                    className="role-permissions-section__toggle"
                    style={{ ...typography.toggle, cursor: 'pointer' }}
                  >
                    {openGroups.includes(group.id) ? '-' : '+'}
                  </span>
                )}
              </div>
            </CardHeader>
            <Collapse isOpen={openGroups.includes(group.id)}>
              <CardBody className='pt-1'>
                <Row>
                  {group.permissions.map((permission) => (
                    <Col md="4" key={permission.id} className="mb-2">
                      <FormGroup check className="mt-2">
                        <Label
                          check
                          htmlFor={`perm_${permission.id}`}
                          style={{ color: permissionLabelColor }}
                        >
                          <Input
                            type="checkbox"
                            id={`perm_${permission.id}`}
                            checked={selectedPermissions.includes(permission.id)}
                            onChange={e => handlePermissionChange(permission.id, e.target.checked)}
                          />
                          <span className="form-check-sign" />
                          {permission.name}
                        </Label>
                      </FormGroup>
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Collapse>
          </Card>
        ))}
      </div>
    </div>
  );
} 